import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Table,
} from "react-bootstrap";
import {
  FaRocket,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFileAlt,
  FaDownload,
  FaBrain,
} from "react-icons/fa";
import Layout from "./Layout";
import axios from "axios";
import "../assets/css/SpaSingleKeyword.css";

const SpKeywordGroup = () => {
  // API Configuration
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

  // Estados para archivos
  const [keywordsFile, setKeywordsFile] = useState(null);
  const [asinsFile, setAsinsFile] = useState(null);
  const [negativeFile, setNegativeFile] = useState(null);

  // Estado para query de agrupamiento
  const [groupingQuery, setGroupingQuery] = useState("");

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [creationComplete, setCreationComplete] = useState(false);
  const [campaignState, setCampaignState] = useState("PAUSED");

  // Bulk actions states
  const [bulkAction, setBulkAction] = useState("");
  const [bulkBudget, setBulkBudget] = useState("");
  const [bulkStartDate, setBulkStartDate] = useState("");
  const [bulkEndDate, setBulkEndDate] = useState("");

  // Manejar carga de archivos
  const handleKeywordsFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setKeywordsFile(file);
    }
  };

  const handleAsinsFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setAsinsFile(file);
    }
  };

  const handleNegativeFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setNegativeFile(file);
    }
  };

  // Generar campañas con agrupamiento AI
  const handleGenerateCampaigns = async () => {
    if (!keywordsFile || !asinsFile) {
      setError("Debes subir los archivos de Keywords y ASINs");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setAnalysis(null);

    try {
      const formData = new FormData();
      formData.append("keywords", keywordsFile);
      formData.append("asins", asinsFile);
      formData.append("groupingQuery", groupingQuery);

      const response = await axios.post(
        `${API_URL}/keyword-grouping/generate`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        const generatedCampaigns = response.data.campaigns || [];

        // Agregar campo 'selected' a cada campaña
        const campaignsWithSelection = generatedCampaigns.map((campaign) => ({
          ...campaign,
          selected: true,
        }));

        setCampaigns(campaignsWithSelection);
        setAnalysis({
          grouping_analysis: response.data.grouping_analysis,
          groups_summary: response.data.groups_summary,
          total_groups: response.data.total_groups,
        });

        setResult({
          success: true,
          message: `${generatedCampaigns.length} campañas generadas en ${response.data.total_groups} grupo(s). Selecciona cuáles crear en Amazon.`,
          count: generatedCampaigns.length,
        });
      } else {
        setError(response.data.error || "Error al generar campañas");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.response?.data?.error || err.message || "Error al generar campañas",
      );
    } finally {
      setLoading(false);
    }
  };

  // Crear campañas en Amazon
  const handleCreateInAmazon = async () => {
    const selectedIndices = campaigns
      .map((c, idx) => (c.selected ? idx : null))
      .filter((idx) => idx !== null);

    if (selectedIndices.length === 0) {
      setError("Debes seleccionar al menos una campaña para crear en Amazon");
      return;
    }

    if (!keywordsFile || !asinsFile) {
      setError("Los archivos originales de Keywords y ASINs son requeridos");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const profileId = localStorage.getItem("selectedAccountValue");

      if (!profileId) {
        setError(
          "Por favor selecciona una cuenta en el selector de arriba antes de crear campañas",
        );
        setLoading(false);
        return;
      }

      // Send FormData with files and selected indices
      const formData = new FormData();
      formData.append("keyword_file", keywordsFile);
      formData.append("asin_file", asinsFile);
      if (negativeFile) {
        formData.append("negative_file", negativeFile);
      }
      formData.append("profileId", profileId);
      formData.append("campaignState", campaignState);
      formData.append(
        "selected_campaign_indices",
        JSON.stringify(selectedIndices),
      );
      formData.append("groupingQuery", groupingQuery);

      const response = await axios.post(
        `${API_URL}/spa-campaigns/create-in-amazon`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        setResult({
          success: true,
          message: `Creadas ${response.data.campaigns_created} campañas en Amazon. ${response.data.campaigns_failed || 0} errores.`,
          count: response.data.campaigns_created,
          amazonResults: response.data.results || [],
          errors: response.data.errors || [],
        });
        setCreationComplete(true);
      } else {
        setError(response.data.error || "Error al crear campañas en Amazon");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Error al crear campañas en Amazon",
      );
    } finally {
      setLoading(false);
    }
  };

  // Toggle selección
  const toggleCampaignSelection = (index) => {
    const updatedCampaigns = [...campaigns];
    updatedCampaigns[index].selected = !updatedCampaigns[index].selected;
    setCampaigns(updatedCampaigns);
  };

  const selectAllCampaigns = () => {
    const updatedCampaigns = campaigns.map((c) => ({ ...c, selected: true }));
    setCampaigns(updatedCampaigns);
  };

  const deselectAllCampaigns = () => {
    const updatedCampaigns = campaigns.map((c) => ({ ...c, selected: false }));
    setCampaigns(updatedCampaigns);
  };

  // Aplicar acción masiva
  const applyBulkAction = () => {
    if (!bulkAction) {
      setError("Selecciona una acción masiva");
      return;
    }

    const selectedCount = campaigns.filter((c) => c.selected).length;
    if (selectedCount === 0) {
      setError("Selecciona al menos una campaña");
      return;
    }

    const updatedCampaigns = campaigns.map((campaign) => {
      if (!campaign.selected) return campaign;

      const updated = { ...campaign };

      switch (bulkAction) {
        case "budget":
          if (bulkBudget && parseFloat(bulkBudget) > 0) {
            updated.Budget = parseFloat(bulkBudget);
          }
          break;
        case "startDate":
          if (bulkStartDate) {
            updated["Start Date"] = bulkStartDate;
          }
          break;
        case "endDate":
          if (bulkEndDate) {
            updated["End Date"] = bulkEndDate;
          }
          break;
        default:
          break;
      }

      return updated;
    });

    setCampaigns(updatedCampaigns);
    setBulkAction("");
    setBulkBudget("");
    setBulkStartDate("");
    setBulkEndDate("");
    setError(null);
  };

  // Descargar CSV
  const handleDownloadCampaigns = () => {
    if (campaigns.length === 0) return;

    const headers = Object.keys(campaigns[0]);
    const csvContent = [
      headers.join(","),
      ...campaigns.map((campaign) =>
        headers
          .map((header) => {
            const value = campaign[header];
            if (typeof value === "string" && value.includes(",")) {
              return `"${value}"`;
            }
            return value;
          })
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `sp_keyword_group_campaigns_${Date.now()}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Reiniciar
  const handleReset = () => {
    setKeywordsFile(null);
    setAsinsFile(null);
    setNegativeFile(null);
    setGroupingQuery("");
    setCampaigns([]);
    setResult(null);
    setError(null);
    setAnalysis(null);
    setCreationComplete(false);
  };

  return (
    <Layout>
      <Container fluid className="spa-keyword-campaigns-page py-4">
        <Row className="mb-4">
          <Col>
            <h2 className="text-white mb-3">
              <FaBrain className="me-2" />
              SP Keyword Campaigns - AI Grouping
            </h2>
            <p className="text-muted">
              Genera campañas SP con agrupamiento inteligente de keywords por
              Search Volume
            </p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <FaExclamationTriangle className="me-2" />
            {error}
          </Alert>
        )}

        {/* Upload Section */}
        {!result && (
          <>
            {/* AI Grouping Query */}
            <div className="config-section-card mb-4">
              <h5 className="mb-3">
                <FaBrain className="me-2 text-primary" />
                Agrupamiento con IA (Opcional)
              </h5>
              <Form.Control
                type="text"
                placeholder="Ej: search volume mayor a 30k, sv entre 10k y 50k, keywords con sv menor a 5000..."
                value={groupingQuery}
                onChange={(e) => setGroupingQuery(e.target.value)}
                className="custom-input"
              />
              <small className="text-muted mt-2 d-block">
                Describe cómo quieres agrupar las keywords. Usa lenguaje natural
                como "search volume mayor a 30k" o "sv entre 10k y 50k".
              </small>
            </div>

            {/* Upload Grid */}
            <div className="upload-grid">
              {/* Card 1: Keywords */}
              <div
                className={`upload-card-new ${keywordsFile ? "has-file" : ""}`}
              >
                <div className="card-required-badge">Requerido</div>
                <div className="upload-icon">
                  <FaFileAlt />
                </div>
                <div className="upload-card-title">Keywords CSV</div>
                <div className="upload-card-desc">
                  Columnas: Keyword, SV (Search Volume), Sugg Bid, Match Type
                </div>
                <label className="w-100 mt-auto">
                  <div className="upload-btn">
                    {keywordsFile ? "Cambiar Archivo" : "Seleccionar Archivo"}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleKeywordsFileChange}
                    hidden
                  />
                </label>
                {keywordsFile && (
                  <small className="text-success mt-2">
                    {keywordsFile.name}
                  </small>
                )}
              </div>

              {/* Card 2: ASINs */}
              <div className={`upload-card-new ${asinsFile ? "has-file" : ""}`}>
                <div className="card-required-badge">Requerido</div>
                <div className="upload-icon">
                  <FaFileAlt />
                </div>
                <div className="upload-card-title">ASINs CSV</div>
                <div className="upload-card-desc">
                  Columnas: ASIN, SKU, Abbreviation
                </div>
                <label className="w-100 mt-auto">
                  <div className="upload-btn">
                    {asinsFile ? "Cambiar Archivo" : "Seleccionar Archivo"}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleAsinsFileChange}
                    hidden
                  />
                </label>
                {asinsFile && (
                  <small className="text-success mt-2">{asinsFile.name}</small>
                )}
              </div>

              {/* Card 3: Negative (Optional) */}
              <div
                className={`upload-card-new ${negativeFile ? "has-file" : ""}`}
              >
                <div className="upload-icon">
                  <FaFileAlt />
                </div>
                <div className="upload-card-title">
                  Negative Targeting (Opcional)
                </div>
                <div className="upload-card-desc">
                  Columnas: Abbreviation, Negative Targeting, Match Type
                </div>
                <label className="w-100 mt-auto">
                  <div className="upload-btn">
                    {negativeFile ? "Cambiar Archivo" : "Seleccionar Archivo"}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleNegativeFileChange}
                    hidden
                  />
                </label>
                {negativeFile && (
                  <small className="text-success mt-2">
                    {negativeFile.name}
                  </small>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <div className="generate-section">
              <button
                className={`btn-generate-campaigns ${!loading && keywordsFile && asinsFile ? "ready" : ""}`}
                onClick={handleGenerateCampaigns}
                disabled={loading || !keywordsFile || !asinsFile}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Generando campañas con IA...
                  </>
                ) : (
                  <>
                    <FaBrain className="me-2" />
                    Generar Campañas con AI Grouping
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {/* Analysis Section */}
        {analysis && (
          <Alert variant="info" className="mb-3">
            <strong>🧠 Análisis IA:</strong> {analysis.grouping_analysis} |
            <strong> Grupos:</strong> {analysis.total_groups} |
            <strong> Resumen:</strong> {analysis.groups_summary}
          </Alert>
        )}

        {/* Results Section */}
        {result && campaigns.length > 0 && (
          <Row>
            <Col>
              <Card className="config-section-card border-0 p-0 overflow-hidden">
                <Card.Header className="bg-dark text-white p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>
                      <FaBrain className="me-2" />
                      Campañas Generadas ({campaigns.length})
                    </h5>
                  </div>

                  <div>
                    {/* Bulk Actions Row */}
                    <div className="mb-3 d-flex justify-content-between align-items-center">
                      <div className="d-flex gap-2 align-items-center">
                        <span className="text-white fw-bold">
                          Bulk actions:
                        </span>
                        <Form.Select
                          size="sm"
                          value={bulkAction}
                          onChange={(e) => setBulkAction(e.target.value)}
                          style={{ width: "200px" }}
                        >
                          <option value="">Seleccionar acción</option>
                          <option value="budget">Asignar presupuesto...</option>
                          <option value="startDate">
                            Asignar Start Date...
                          </option>
                          <option value="endDate">Asignar End Date...</option>
                        </Form.Select>

                        {bulkAction === "budget" && (
                          <Form.Control
                            type="number"
                            size="sm"
                            placeholder="Nuevo presupuesto"
                            value={bulkBudget}
                            onChange={(e) => setBulkBudget(e.target.value)}
                            style={{ width: "150px" }}
                            min="0"
                            step="0.01"
                          />
                        )}

                        {bulkAction === "startDate" && (
                          <Form.Control
                            type="date"
                            size="sm"
                            value={bulkStartDate}
                            onChange={(e) => setBulkStartDate(e.target.value)}
                            style={{ width: "150px" }}
                          />
                        )}

                        {bulkAction === "endDate" && (
                          <Form.Control
                            type="date"
                            size="sm"
                            value={bulkEndDate}
                            onChange={(e) => setBulkEndDate(e.target.value)}
                            style={{ width: "150px" }}
                          />
                        )}

                        {bulkAction && (
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={applyBulkAction}
                          >
                            Aplicar
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons Row */}
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Select
                          size="sm"
                          value={campaignState}
                          onChange={(e) => setCampaignState(e.target.value)}
                          style={{ width: "auto" }}
                          disabled={creationComplete}
                        >
                          <option value="ENABLED">Crear como: Enabled</option>
                          <option value="PAUSED">Crear como: Paused</option>
                        </Form.Select>

                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={handleDownloadCampaigns}
                        >
                          <FaDownload className="me-1" /> Descargar CSV
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleReset}
                        >
                          <FaRocket className="me-1" /> Nueva Generación
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body
                  className="p-0 table-responsive bg-dark"
                  style={{ maxHeight: "600px", overflowY: "auto" }}
                >
                  <Table
                    hover
                    variant="dark"
                    className="align-middle mb-0"
                    size="sm"
                    style={{ fontSize: "0.85rem" }}
                  >
                    <thead className="text-warning sticky-top bg-dark">
                      <tr>
                        <th width="40">
                          <Form.Check
                            type="checkbox"
                            checked={campaigns.every((c) => c.selected)}
                            onChange={(e) =>
                              e.target.checked
                                ? selectAllCampaigns()
                                : deselectAllCampaigns()
                            }
                          />
                        </th>
                        <th width="50">#</th>
                        <th width="250">Campaign Name</th>
                        <th width="120">Keyword</th>
                        <th width="80">Match</th>
                        <th width="80">Bid</th>
                        <th width="80">Budget</th>
                        <th width="80">SV</th>
                        <th width="100">Group</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((campaign, idx) => (
                        <tr key={idx}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={campaign.selected || false}
                              onChange={() => toggleCampaignSelection(idx)}
                            />
                          </td>
                          <td>{idx + 1}</td>
                          <td>
                            <div
                              className="text-truncate"
                              style={{ maxWidth: "230px" }}
                            >
                              {campaign["Campaign Name"]}
                            </div>
                          </td>
                          <td className="text-info">{campaign.Keyword}</td>
                          <td>
                            <Badge bg="secondary">
                              {campaign["Match Type"]}
                            </Badge>
                          </td>
                          <td>${campaign.Bid}</td>
                          <td>${campaign.Budget}</td>
                          <td>
                            {campaign["Search Volume"]?.toLocaleString() || 0}
                          </td>
                          <td>
                            <Badge bg="primary">
                              {campaign.Group || "Default"}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
                <Card.Footer className="bg-dark border-top border-secondary d-flex justify-content-between p-3">
                  <div className="d-flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={handleReset}
                      className="fw-bold"
                    >
                      ← Cancelar
                    </Button>
                  </div>
                  <div className="d-flex gap-3 align-items-center">
                    <div className="text-muted small">
                      Total: {campaigns.length} | Seleccionadas:{" "}
                      {campaigns.filter((c) => c.selected).length}
                    </div>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleCreateInAmazon}
                      disabled={
                        loading ||
                        creationComplete ||
                        campaigns.filter((c) => c.selected).length === 0
                      }
                      style={{
                        fontWeight: "bold",
                        padding: "6px 20px",
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-1"
                          />
                          Creando en Amazon...
                        </>
                      ) : creationComplete ? (
                        <>
                          <FaCheckCircle className="me-1" /> Campañas Creadas
                        </>
                      ) : (
                        <>Crear Campañas Seleccionadas</>
                      )}
                    </Button>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        )}

        {/* Requirements Section */}
        {!result && (
          <div className="requirements-section">
            <span className="requirements-title">
              📋 Requisitos de los archivos CSV
            </span>
            <div className="req-item">
              <strong>Keywords CSV:</strong> Columnas 'Keyword', 'SV' (Search
              Volume), 'Sugg Bid', 'Match Type'.
            </div>
            <div className="req-item">
              <strong>ASINs CSV:</strong> Columnas 'ASIN', 'SKU'. Opcional:
              'Abbreviation'.
            </div>
            <div className="req-item">
              <strong>Query IA:</strong> Usa lenguaje natural como "sv mayor a
              30k" o "search volume entre 10k y 50k".
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default SpKeywordGroup;
